/**
 * Formato de exportación e importación (FR-DATA-01/03). Versionado y
 * autodescriptivo. Incluye las marcas HLC para que una importación fusione
 * sin duplicar ni retroceder cambios.
 */
import { z } from 'zod';
import { ENTITIES, type EntityName } from './entities.js';
import { validateRecord, type StoredRecord } from './sync.js';

export const EXPORT_FORMAT = 'habitos-export';
export const EXPORT_SCHEMA_VERSION = 1;

/**
 * Procedencia del archivo (opcional, añadida en 0.2 sin cambiar schemaVersion:
 * los lectores anteriores la ignoran). Permite avisar al importar datos de otra cuenta.
 */
export interface ExportSource {
  kind: 'device' | 'server';
  accountId: string | null;
  accountEmail: string | null;
}

export interface ExportFile {
  format: typeof EXPORT_FORMAT;
  schemaVersion: number;
  exportedAt: string;
  appVersion: string;
  source?: ExportSource;
  /** Datos personales de la cuenta (solo en la exportación del servidor). No se importan. */
  account?: Record<string, unknown>;
  records: Record<EntityName, { id: string; data: Record<string, unknown>; hlc: Record<string, string> }[]>;
}

const sourceSchema = z.object({
  kind: z.enum(['device', 'server']),
  accountId: z.string().uuid().nullable(),
  accountEmail: z.string().max(254).nullable(),
});

const recordSchema = z.object({
  id: z.string().min(1).max(64),
  data: z.record(z.unknown()),
  hlc: z.record(z.string()),
});

export const exportFileSchema = z.object({
  format: z.literal(EXPORT_FORMAT),
  schemaVersion: z.number().int().min(1),
  exportedAt: z.string(),
  appVersion: z.string().max(40),
  records: z.object(Object.fromEntries(ENTITIES.map((e) => [e, z.array(recordSchema).max(200_000).default([])])) as Record<EntityName, z.ZodDefault<z.ZodArray<typeof recordSchema>>>),
});

export function buildExport(
  records: StoredRecord[],
  appVersion: string,
  now = new Date(),
  extra: { source?: ExportSource; account?: Record<string, unknown> } = {},
): ExportFile {
  const grouped = Object.fromEntries(ENTITIES.map((e) => [e, [] as ExportFile['records'][EntityName]])) as ExportFile['records'];
  for (const r of records) grouped[r.entity].push({ id: r.id, data: r.data, hlc: r.hlc });
  return {
    format: EXPORT_FORMAT,
    schemaVersion: EXPORT_SCHEMA_VERSION,
    exportedAt: now.toISOString(),
    appVersion,
    ...(extra.source ? { source: extra.source } : {}),
    ...(extra.account ? { account: extra.account } : {}),
    records: grouped,
  };
}

export interface ParsedImport {
  source: ExportSource | null;
  records: StoredRecord[];
  invalid: { entity: EntityName; id: string; detail: string }[];
  /** Tipos de datos que esta versión no reconoce (p. ej., de una versión futura): no se importan. */
  unknown: { entity: string; count: number }[];
  counts: Record<EntityName, number>;
}

export class ImportError extends Error {
  constructor(readonly code: 'not_json' | 'wrong_format' | 'newer_version' | 'invalid_structure', message: string) {
    super(message);
  }
}

/** Valida un archivo de exportación. Los registros inválidos se informan, no se importan. */
export function parseImport(text: string): ParsedImport {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new ImportError('not_json', 'El archivo no es JSON válido');
  }
  if (typeof raw !== 'object' || raw === null || (raw as { format?: unknown }).format !== EXPORT_FORMAT) {
    throw new ImportError('wrong_format', 'El archivo no es una exportación de HabitOS');
  }
  const version = (raw as { schemaVersion?: unknown }).schemaVersion;
  if (typeof version === 'number' && version > EXPORT_SCHEMA_VERSION) {
    throw new ImportError('newer_version', 'El archivo procede de una versión más reciente de la aplicación');
  }
  const parsed = exportFileSchema.safeParse(raw);
  if (!parsed.success) throw new ImportError('invalid_structure', 'La estructura del archivo no es válida');
  const src = sourceSchema.safeParse((raw as { source?: unknown }).source);
  const out: ParsedImport = {
    source: src.success ? src.data : null,
    records: [],
    invalid: [],
    unknown: Object.entries(((raw as { records?: unknown }).records ?? {}) as Record<string, unknown>)
      .filter(([entity]) => !(ENTITIES as readonly string[]).includes(entity))
      .map(([entity, list]) => ({ entity: entity.slice(0, 40), count: Array.isArray(list) ? list.length : 0 })),
    counts: Object.fromEntries(ENTITIES.map((e) => [e, 0])) as Record<EntityName, number>,
  };
  for (const entity of ENTITIES) {
    for (const r of parsed.data.records[entity]) {
      const v = validateRecord(entity, r.id, r.data);
      const hlcOk = Object.keys(r.data).every((k) => !v.ok || !(k in v.data) || typeof r.hlc[k] === 'string');
      if (!v.ok || !hlcOk) {
        out.invalid.push({ entity, id: r.id, detail: v.ok ? 'marcas HLC incompletas' : v.detail });
        continue;
      }
      const hlc: Record<string, string> = {};
      for (const k of Object.keys(v.data)) if (r.hlc[k]) hlc[k] = r.hlc[k]!;
      out.records.push({ entity, id: r.id, data: v.data, hlc });
      out.counts[entity]++;
    }
  }
  return out;
}

function csvCell(v: unknown, sep: string): string {
  const s = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /["\n\r]/.test(s) || s.includes(sep) || /^[=+\-@]/.test(s) ? `"${s.replace(/^([=+\-@])/, "'$1").replace(/"/g, '""')}"` : s;
}

/** CSV con protección contra inyección de fórmulas en hojas de cálculo. */
export function toCSV(rows: Record<string, unknown>[], columns: string[], sep = ','): string {
  const lines = [columns.join(sep)];
  for (const row of rows) lines.push(columns.map((c) => csvCell(row[c], sep)).join(sep));
  return lines.join('\r\n') + '\r\n';
}
